import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import DashboardCommercials from "@/pages/DashboardCommercials";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import DeletedProjects from "@/pages/DeletedProjects";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import ContactDetail from "@/pages/ContactDetail";
import ClientOnboarding from "@/pages/ClientOnboarding";
import MasterServiceAgreements from "@/pages/MasterServiceAgreements";
import Payments from "@/pages/Payments";
import Settings from "@/pages/Settings";
import Help from "@/pages/Help";
import Documents from "@/pages/Documents";
import MarksManDocuments from "@/pages/MarksManDocuments";
import { Redirect } from "@/components/shared/Redirect";
import ConferenceContacts from "@/pages/ConferenceContacts";
import ConferenceContactDetail from "@/pages/ConferenceContactDetail";
import ConferenceDetail from "@/pages/ConferenceDetail";
import SearchResults from "@/pages/SearchResults";
import People from "@/pages/People";
import PersonDetail from "@/pages/PersonDetail";

const Protected = ({ children, roles }) => {
  const { user } = useAuth();
  if (user && roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm" data-testid="auth-loading">
        Loading…
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/dashboard/commercials" element={<Protected><DashboardCommercials /></Protected>} />
            <Route path="/projects" element={<Protected><Projects /></Protected>} />
            <Route path="/projects/deleted" element={<Protected><DeletedProjects /></Protected>} />
            <Route path="/projects/:id" element={<Protected><ProjectDetail /></Protected>} />
            <Route path="/clients" element={<Protected><Clients /></Protected>} />
            <Route path="/clients/:id" element={<Protected><ClientDetail /></Protected>} />
            <Route path="/contacts/:id" element={<Protected><ContactDetail /></Protected>} />
            <Route path="/payments" element={<Protected roles={["owner", "admin", "pm", "finance"]}><Payments /></Protected>} />
            <Route path="/settings" element={<Protected roles={["owner", "admin"]}><Settings /></Protected>} />
            <Route path="/people" element={<Protected><People /></Protected>} />
            <Route path="/people/:id" element={<Protected><PersonDetail /></Protected>} />
            <Route path="/help" element={<Protected><Help /></Protected>} />
            <Route path="/documents" element={<Protected><Documents /></Protected>} />
            <Route path="/conference-contacts" element={<Protected><ConferenceContacts /></Protected>} />
            <Route path="/conference-contacts/conference/:id" element={<Protected><ConferenceDetail /></Protected>} />
            <Route path="/conference-contacts/:id" element={<Protected><ConferenceContactDetail /></Protected>} />
            <Route path="/marksman-documents" element={<Protected><MarksManDocuments /></Protected>} />
            <Route path="/client-onboarding" element={<Protected><ClientOnboarding /></Protected>} />
            <Route path="/master-service-agreements" element={<Protected><MasterServiceAgreements /></Protected>} />
            <Route path="/company-documents" element={<Redirect to="/marksman-documents?tab=certifications" />} />
            <Route path="/onboarding-documents" element={<Redirect to="/marksman-documents?tab=corporate" />} />
            <Route path="/client-agreements" element={<Redirect to="/master-service-agreements" />} />
            <Route path="/policies" element={<Redirect to="/marksman-documents?tab=policies" />} />
            <Route path="/search" element={<Protected><SearchResults /></Protected>} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
